// LessonBookingCard.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Card, CardContent, Box, Typography, Chip, TextField,
  Select, MenuItem, Modal, Button, IconButton
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import TLHClassesAPICalls from "services/TLHClassesAPICalls.js";
import TLHEndpoints from "services/TLHEndpoints.js";

export const LEVEL_OPTIONS = [
  "RECEPTION","YEAR_1","YEAR_2","YEAR_3","YEAR_4","YEAR_5","YEAR_6",
  "YEAR_7_AND_8","YEAR_8_AND_9","KS3_A","KS3_B","KS3_C",
  "GCSE_1","GCSE_2","AS_LEVEL","A2_LEVEL",
];

/**
 * Props:
 * - lessonNumber: number|string
 * - level: one of LEVEL_OPTIONS
 * - lessonPlanNumber: number (1..100)
 * - date: "YYYY-MM-DD"
 * - time: "HH:mm"
 * - status: "attended" | "missed" | "neutral"   // controls card colour + bottom label
 * - onDateChange?: (v: string) => void
 * - onTimeChange?: (v: string) => void
 * - onLevelChange?: (v: string) => void
 * - onLessonPlanChange?: (v: number) => void
 * - sx?: object
 */
export default function LessonBookingCard({
  lessonNumber,
  level,
  lessonPlanNumber,
  date,
  time,
  classLessonRecordId,
  status: propStatus = "neutral",
  onDateChange,
  onTimeChange,
  onLevelChange,
  onLessonPlanChange,
  onStatusChange,
  sx,
}) {
  const [editing, setEditing] = useState(null); // 'date' | 'time' | 'level' | 'plan'
  const [futureLessonModalOpen, setFutureLessonModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [teacherCopies, setTeacherCopies] = useState(1);
  const [studentCopies, setStudentCopies] = useState(1);
  const inputRef = useRef(null);
  // local status state — prefer controlled prop when provided
  const [localStatus, setLocalStatus] = useState(propStatus || "neutral");

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus?.();
  }, [editing]);

  // keep localStatus in sync when parent changes propStatus
  useEffect(() => {
    setLocalStatus(propStatus || "neutral");
  }, [propStatus]);

  const today = new Date(new Date().toDateString());
  const lessonDate = date ? new Date(date) : null;
  const isFuture = lessonDate ? lessonDate > today : false;
  const isPast   = lessonDate ? lessonDate < today : false;

  const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday ...
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Monday
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
  const isThisWeek = lessonDate ? lessonDate >= startOfWeek && lessonDate <= endOfWeek : false;

  const status = localStatus;
  const { bg, border, label, labelColor } = getStatusStyles(status);

  const handleKey = (e) => {
    if (e.key === "Escape") setEditing(null);
    if (e.key === "Enter") e.currentTarget.blur();
  };

  // Future: blocked (modal shown)
  // Past:   neutral/missed → attended → missed → attended (no neutral)
  // Today:  neutral → attended → missed → neutral
  function cycleStatus(classLessonRecordId) {
    if (isFuture) {
      setFutureLessonModalOpen(true);
      return;
    }
    const next = isPast
      ? (status === "attended" ? "missed" : "attended")
      : (status === "neutral" ? "attended" : status === "attended" ? "missed" : "neutral");
    // if we have a server id, attempt an async update and rollback on failure

    console.log("[cycleStatus] classLessonRecordId:", classLessonRecordId, "type:", typeof classLessonRecordId);
    console.log("[cycleStatus] current status:", status, "→ next:", next);
    console.log("[cycleStatus] isFuture:", isFuture, "isPast:", isPast, "date:", date);
    if (typeof classLessonRecordId !== "undefined" && classLessonRecordId !== null) {
      TLHClassesAPICalls.updateOneToOneAttendance(next, classLessonRecordId)
        .then((res) => {
          console.log("[cycleStatus] server response status:", res.status, "data:", res.data);
          if (res.status != 200) {
            throw new Error("updateOneToOneAttendance did not return OK");
          }
        })
        .catch((err) => {
          console.error("[cycleStatus] Failed to update attendance on server:", err);
          console.error("[cycleStatus] Error response data:", err.response?.data);
          console.error("[cycleStatus] Error response status:", err.response?.status);
          // revert local UI to previous status
          setLocalStatus(status);
          if (onStatusChange) onStatusChange(status);
        });
    } else {
      console.warn("[cycleStatus] skipping API call — classLessonRecordId is", classLessonRecordId);
    }
    setLocalStatus(next);
    if (onStatusChange) onStatusChange(next);
  }

  return (
    <>
    <Card
      variant="outlined"
      onClick={(e) => {
        // toggle status only when clicking the card itself (not interactive inputs)
        // ignore clicks from input elements
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA' || e.currentTarget !== e.target && e.target.closest('input,select,textarea,button')) return;
        cycleStatus(classLessonRecordId);
      }}
      sx={{
        width: 152, // roughly one third of previous width
        borderRadius: 2,
        bgcolor: bg,
        borderColor: border,
        boxShadow: isThisWeek ? "0 2px 8px rgba(0,0,0,0.15)" : "0 1px 6px rgba(0,0,0,0.04)",
        "&:hover": { boxShadow: "0 4px 10px rgba(0,0,0,0.08)" },
        cursor: 'pointer',
        borderWidth: isThisWeek ? 3 : 1,
        borderColor: isThisWeek ? '#000000' : border,
        padding: 0,
        ...sx,
      }}
    >
      <CardContent sx={{ p: 1, pb: 0.75, mb: -1 }}>
        {/* Top: Lesson # (centered) */}
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 0.5 }}>
          <Chip label={`Lesson ${lessonNumber}`} size="small" sx={{ fontWeight: 700, letterSpacing: 0.2, px: 1 }} />
        </Box>

        {/* Middle: Level (editable) + Lesson Plan # (editable) */}
        <Box sx={{ mt: 0.25, mb: 1, display: "grid", rowGap: 1 }}>
          {/* Level */}
          <Box>
            {/* label removed to save vertical space */}
            {editing === "level" ? (
              <Select
                inputRef={inputRef}
                size="small"
                value={level || ""}
                onChange={(e) => {
                  onLevelChange && onLevelChange(e.target.value);
                  setEditing(null);
                }}
                onBlur={() => setEditing(null)}
                fullWidth
              >
                {LEVEL_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{pretty(opt)}</MenuItem>
                ))}
              </Select>
            ) : (
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                onClick={(e) => { e.stopPropagation(); setEditing("level"); }}
              >
                {pretty(level) || "—"}
              </Typography>
            )}
          </Box>

          {/* Lesson Plan Number */}
          <Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.75, mb: 0.25 }}>
              Lesson Plan
            </Typography>
            {editing === "plan" ? (
              <TextField
                inputRef={inputRef}
                type="number"
                size="small"
                defaultValue={lessonPlanNumber ?? ""}
                inputProps={{ min: 1, max: 100, step: 1 }}
                onBlur={(e) => {
                  const n = clampInt(e.target.value, 1, 100);
                  if (n != null && onLessonPlanChange) onLessonPlanChange(n);
                  setEditing(null);
                }}
                onKeyDown={handleKey}
                variant="outlined"
              />
            ) : (
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                onClick={(e) => { e.stopPropagation(); setEditing("plan"); }}
              >
                {lessonPlanNumber ?? "—"}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Bottom block: Date (left) / Time (right) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center",
            pt: 0.5,
            borderTop: "1px solid",
            borderColor: "divider",
            columnGap: 0.5,
          }}
        >
          {/* Date (editable) */}
          <Box sx={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setEditing("date"); }}>
            {editing === "date" ? (
              <TextField
                inputRef={inputRef}
                type="date"
                size="small"
                defaultValue={date || ""}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (v && onDateChange) onDateChange(v);
                  setEditing(null);
                }}
                onKeyDown={handleKey}
                variant="standard"
                fullWidth
                sx={{ fontSize: '0.7rem' }}
              />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                {formatDateDisplay(date) || "—"}
              </Typography>
            )}
          </Box>

          {/* Time (editable) */}
          <Box sx={{ textAlign: "right", cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setEditing("time"); }}>
            {editing === "time" ? (
              <TextField
                inputRef={inputRef}
                type="time"
                size="small"
                defaultValue={time || ""}
                onBlur={(e) => {
                  const v = e.target.value;
                  if (v && onTimeChange) onTimeChange(v);
                  setEditing(null);
                }}
                onKeyDown={handleKey}
                variant="standard"
                sx={{ fontSize: '0.7rem' }}
              />
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                {time || "—"}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Status label + print button */}
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: labelColor, fontSize: '0.65rem', flex: 1, textAlign: 'center' }}>
            {label}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setPrintModalOpen(true); }}
            sx={{ p: 0.25, color: 'text.secondary' }}
            title="Print lesson plan"
          >
            <PrintIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Box>
      </CardContent>
    </Card>

    <Modal open={printModalOpen} onClose={() => setPrintModalOpen(false)}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper', borderRadius: 2,
        boxShadow: 24, p: 4, width: 300,
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Print Lesson Plan</Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Lesson Plan {lessonPlanNumber} — {pretty(level)}
        </Typography>
        <TextField
          label="Teacher Copies"
          type="number"
          fullWidth
          value={teacherCopies}
          onChange={(e) => setTeacherCopies(Math.max(0, parseInt(e.target.value) || 0))}
          inputProps={{ min: 0 }}
          margin="normal"
          size="small"
        />
        <TextField
          label="Student Copies"
          type="number"
          fullWidth
          value={studentCopies}
          onChange={(e) => setStudentCopies(Math.max(0, parseInt(e.target.value) || 0))}
          inputProps={{ min: 0 }}
          margin="normal"
          size="small"
        />
        <Button
          variant="contained"
          fullWidth
          sx={{ mt: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            const modalContent = {
              lessonNumber: lessonPlanNumber,
              studentsCount: 1,
              lessonId: classLessonRecordId,
            };
            TLHEndpoints.printLessonPlans(modalContent, teacherCopies, studentCopies)
              .then(() => {
                setPrintModalOpen(false);
                setTeacherCopies(1);
                setStudentCopies(1);
              })
              .catch(err => console.error('Print request failed:', err));
          }}
        >
          Print
        </Button>
      </Box>
    </Modal>

    <Modal open={futureLessonModalOpen} onClose={() => setFutureLessonModalOpen(false)}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper', borderRadius: 2,
        boxShadow: 24, p: 4, width: 340, textAlign: 'center',
      }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Cannot Update Status</Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          The status of a future lesson cannot be updated. Please wait until the lesson date has passed.
        </Typography>
        <Button variant="contained" onClick={() => setFutureLessonModalOpen(false)}>OK</Button>
      </Box>
    </Modal>
    </>
  );
}

/* ---- helpers ---- */
function pretty(s) {
  if (!s) return "";
  return s.replace(/_/g, " ").replace(/\b(\w)/g, (m) => m.toUpperCase());
}
function formatDateDisplay(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    if (!y || !m || !d) return iso;
    return `${pad2(d)}/${pad2(m)}/${y}`;
  } catch {
    return iso;
  }
}
function pad2(n) {
  return String(n).padStart(2, "0");
}
function clampInt(v, min, max) {
  if (v == null || v === "") return null;
  const n = parseInt(v, 10);
  if (Number.isNaN(n)) return null;
  return Math.min(max, Math.max(min, n));
}
function getStatusStyles(status) {
  switch (status) {
    case "attended":
      return {
        bg: "#e8f5e9",          // green 50
        border: "#a5d6a7",      // green 200
        label: "Attended",
        labelColor: "#2e7d32",  // green 800
      };
    case "missed":
      return {
        bg: "#ffebee",          // red 50
        border: "#ef9a9a",      // red 200
        label: "Missed",
        labelColor: "#c62828",  // red 800
      };
    default:
      return {
        bg: "#fafafa",          // neutral
        border: "#e0e0e0",
        label: "Upcoming",
        labelColor: "#616161",
      };
  }
}
